import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding, useUpdateOnboarding } from '../hooks/useOnboarding';

interface OnboardingStep {
  key: string;
  title: string;
  description: string;
  cta: string;
  href: string;
}

const STEPS: OnboardingStep[] = [
  {
    key: 'connect_email',
    title: 'Connect your email account',
    description:
      'Send pitches through your own inbox — no "sent via" marks, no deliverability anxiety. Gmail users get one-click OAuth; other providers take a minute with an app password.',
    cta: 'Connect email',
    href: '/outreach',
  },
  {
    key: 'import_contacts',
    title: 'Import your contacts',
    description:
      'Bring in your existing list via CSV or add contacts manually. Your contacts are yours — we never share them.',
    cta: 'Import contacts',
    href: '/contacts',
  },
  {
    key: 'browse_reporters',
    title: 'Browse the reporter database',
    description:
      'Search 350K+ journalists by beat, publication, or coverage history. Find reporters who are actually writing about your space, then import them into your contacts.',
    cta: 'Browse reporters',
    href: '/reporters',
  },
  {
    key: 'create_press_list',
    title: 'Create your first press list',
    description:
      'A press list organises your contacts for a campaign. Mix your own contacts with reporters from the database — add to a list and start pitching.',
    cta: 'Create press list',
    href: '/press-lists',
  },
];

function StepCard({
  step,
  index,
  total,
  onNext,
  onGoTo,
}: {
  step: OnboardingStep;
  index: number;
  total: number;
  onNext: () => void;
  onGoTo: (href: string) => void;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-lg w-full">
      <div className="flex items-center gap-2 mb-6">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i <= index ? 'bg-blue-500' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2">
        Step {index + 1} of {total}
      </p>
      <h2 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h2>
      <p className="text-gray-500 text-sm leading-relaxed mb-8">{step.description}</p>

      <div className="flex flex-col gap-3">
        <button
          onClick={() => onGoTo(step.href)}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
        >
          {step.cta}
        </button>
        <button
          onClick={onNext}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
        >
          {index < total - 1 ? 'Skip this step' : "I'll do this later"}
        </button>
      </div>
    </div>
  );
}

function WelcomeCard({ onStart, onSkip }: { onStart: () => void; onSkip: () => void }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-lg w-full text-center">
      <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <svg className="w-7 h-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-3">Welcome to PitchFriendly</h1>
      <p className="text-gray-500 text-sm leading-relaxed mb-2">
        Enterprise-grade PR tools at a price that makes sense.
      </p>
      <p className="text-gray-500 text-sm leading-relaxed mb-8">
        Find journalists, build targeted lists, and send personalised pitches through your own inbox — all in one place.
      </p>

      <div className="bg-gray-50 rounded-xl p-4 mb-8 text-left space-y-2.5">
        {[
          '350K+ journalists with beat and coverage data',
          'Send pitches through your own email — no "sent via"',
          'Full CRM for your contacts and press lists',
          'Vector search to find reporters covering your space',
        ].map((feature) => (
          <div key={feature} className="flex items-start gap-2.5">
            <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-gray-600">{feature}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        <button
          onClick={onStart}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
        >
          Let's get started
        </button>
        <button
          onClick={onSkip}
          className="w-full text-gray-400 hover:text-gray-600 text-sm py-1 transition-colors"
        >
          Skip setup, take me to the dashboard
        </button>
      </div>
    </div>
  );
}

function CompletionCard({ onFinish }: { onFinish: () => void }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-lg w-full text-center">
      <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-3">You're all set!</h2>
      <p className="text-gray-500 text-sm leading-relaxed mb-8">
        Your setup checklist will live on your dashboard so you can come back to any step whenever you're ready.
      </p>
      <button
        onClick={onFinish}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
      >
        Go to dashboard
      </button>
    </div>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { data } = useOnboarding();
  const updateOnboarding = useUpdateOnboarding();

  // 0 = welcome, 1..N = steps, N+1 = completion
  const [localStep, setLocalStep] = useState<number>(() => {
    const serverStep = data?.data?.step ?? 0;
    return serverStep;
  });

  const currentServerStep = data?.data?.step ?? 0;
  const displayStep = localStep === 0 && currentServerStep === 0 ? 0 : Math.max(localStep, currentServerStep);

  async function advanceTo(step: number) {
    setLocalStep(step);
    await updateOnboarding.mutateAsync({ step });
  }

  async function handleSkipAll() {
    setLocalStep(STEPS.length + 1);
    await updateOnboarding.mutateAsync({ step: STEPS.length + 1, completed: true });
    navigate('/dashboard');
  }

  async function handleStart() {
    await advanceTo(1);
  }

  async function handleNextStep() {
    const next = displayStep + 1;
    if (next > STEPS.length) {
      await updateOnboarding.mutateAsync({ step: next, completed: true });
      setLocalStep(next);
    } else {
      await advanceTo(next);
    }
  }

  async function handleGoTo(href: string) {
    const next = displayStep + 1;
    await advanceTo(next > STEPS.length ? STEPS.length + 1 : next);
    navigate(href);
  }

  async function handleFinish() {
    await updateOnboarding.mutateAsync({ step: STEPS.length + 1, completed: true });
    navigate('/dashboard');
  }

  const stepIndex = displayStep - 1; // 0-based index into STEPS array

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
      <div className="mb-8">
        <span className="text-lg font-bold text-gray-900 tracking-tight">PitchFriendly</span>
      </div>

      {displayStep === 0 && (
        <WelcomeCard onStart={handleStart} onSkip={handleSkipAll} />
      )}

      {displayStep >= 1 && displayStep <= STEPS.length && (
        <StepCard
          step={STEPS[stepIndex]}
          index={stepIndex}
          total={STEPS.length}
          onNext={handleNextStep}
          onGoTo={handleGoTo}
        />
      )}

      {displayStep > STEPS.length && (
        <CompletionCard onFinish={handleFinish} />
      )}
    </div>
  );
}
